import { User } from "@easycv/common/types/user";
import { CCManage } from "../cclib/CCManage";
import { CCMulti } from "../cclib/CCMulti";
import { CCSetup } from "../cclib/CCSetup";
import {
  EasyCVCareerConference,
  EasyCVCareerConferenceManageArgs,
  EasyCVCareerConferenceSetupArgs
} from "../cclib/types/cclib";
import { afcsDataPrefix, outputAFCS, setupAFCS, updateAFCS } from "./afcs/afcs";
import {
  addPersistentClinicalHeader,
  clinicalDataPrefix,
  setupClinical,
  updateClinical
} from "./clinical/clinical";

import { Conference } from "@easycv/common/types/conference";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat.js";
import {
  addPersistentAssistantHeader,
  assistantDataPrefix,
  setupAssistant,
  updateAssistant
} from "./assistant/assistant";
import {
  addPersistentSeniorHeader,
  seniorDataPrefix,
  setupSenior,
  updateSenior
} from "./senior/senior";
import { addDownloadInternalAFCSButton } from "./shared/util";
dayjs.extend(LocalizedFormat);

export const DISABLE_EMAILS = true;

function overrideVerbage(cc: CCSetup) {
  cc.overrideVerbage({
    mentee: "Faculty",
    mentor: "Reviewer",
    closed: "Closed",
    finalized: "Finalized",
    statusTextFn: (status, verbage) => {
      switch (status) {
        case "mentee":
          return `${verbage.mentee} Editing`;
        case "mentor":
          return `In Review`;
        case "closed":
          return verbage.closed;
        case "finalized":
          return verbage.finalized;
      }
    },
    alertTextFn: (status, verbage) => {
      return status === "mentee"
        ? `your ${verbage.mentor.toLowerCase()}`
        : cc.cvOwner.name;
    }
  });
}

function addInstancesToMulti(multi: CCMulti) {
  multi.add({
    title: "AFCS",
    shortTitle: "afcs",
    dataPrefix: afcsDataPrefix,
    onActivate: setupAFCS,
    updateCallback: updateAFCS,
    statusChangeRules: {
      allowSubmit: true,
      allowUnsubmit: true,
      allowFinalize: false,
      allowUnfinalize: false
    }
  });
  multi.add({
    title: "Annual Counseling Meeting Documentation",
    shortTitle: "clinical",
    dataPrefix: clinicalDataPrefix,
    onActivate: setupClinical,
    updateCallback: updateClinical,
    statusChangeRules: {
      allowSubmit: false,
      allowUnsubmit: false,
      allowFinalize: true,
      allowUnfinalize: true
    }
  });
  multi.add({
    title: "Annual Meetings for Senior Faculty",
    shortTitle: "senior",
    dataPrefix: seniorDataPrefix,
    onActivate: setupSenior,
    updateCallback: updateSenior,
    statusChangeRules: {
      allowSubmit: false,
      allowUnsubmit: false,
      allowFinalize: true,
      allowUnfinalize: true
    }
  });
  multi.add({
    title: "Annual Meetings for Assistant and Associate Professors",
    shortTitle: "assistant",
    dataPrefix: assistantDataPrefix,
    onActivate: setupAssistant,
    updateCallback: updateAssistant,
    statusChangeRules: {
      allowSubmit: false,
      allowUnsubmit: false,
      allowFinalize: true,
      allowUnfinalize: true
    }
  });
}

function setupMultiPersist(multi: CCMulti) {
  multi.persist((cc: CCSetup, active) => {
    overrideVerbage(cc);
    cc.appendStandardHeader(
      () => {
        outputAFCS(multi.currentReference());
      },
      {
        ccDownload: "AFCS Form"
      },
      false
    );
    cc.appendAddAttachmentButton(
      "stanford-apr",
      {
        relative: "before",
        selector: "*:has(> [data-attachment-button-mark])"
      },
      "__stanford_afcs_attachment",
      undefined,
      undefined,
      true
    );

    if (cc.checkPermissions().hasExplicitConferenceTabAccess()) {
      void {
        clinical: addPersistentClinicalHeader,
        senior: addPersistentSeniorHeader,
        assistant: addPersistentAssistantHeader
      }[active.shortTitle]?.(cc);
      if (active.shortTitle !== "afcs") {
        addDownloadInternalAFCSButton(cc, active);
      }
    }

    cc.update(cc.struct.structure);
  });
}

function defineMultiRules(multi: CCMulti) {
  const ranks = {
    clinical: [
      "Instructor",
      "Clinical Associate Professor",
      "Clinical Assistant Professor"
    ],
    senior: ["Professor", "Clinical Professor"],
    assistant: ["Assistant Professor", "Associate Professor"]
  };
  for (const [key, rankList] of Object.entries(ranks)) {
    multi.rules(key).visibleIf(() => {
      const current = multi.currentReference();
      const toggleVal =
        current.conference.openYears[current.viewYear]?.[AFCS_TAB_OPEN_KEY];
      // Default to false if undefined
      if (toggleVal === true) {
        const data = current.newDef?.career?.[current.viewYear]?.data;
        // afcs data prefix to match 1st tab's faculty_rank
        const rank = data?.[afcsDataPrefix + "faculty_rank"];
        return (
          rankList.includes(rank) &&
          current.checkPermissions().readableAsMentorIdentity()
        );
      }
      return false;
    });
    multi.rules(key).accessibleIf(() => {
      const current = multi.currentReference();
      const toggleVal =
        current.conference.openYears[current.viewYear]?.[AFCS_TAB_OPEN_KEY];
      // Default to false if undefined
      if (toggleVal === true) {
        return current.yearData.status !== "mentee";
      }
      return false;
    });
  }
}

export default {
  title: "Stanford Emergency Medicine",
  tabTitle: "AFCS",
  cvSetup: function ({
    contextMethods,
    contextConstants,
    contextState,
    conference,
    backend
  }: EasyCVCareerConferenceSetupArgs) {
    const multi = new CCMulti(
      conference,
      backend,
      contextMethods,
      contextConstants,
      contextState
    );
    addInstancesToMulti(multi);
    setupMultiPersist(multi);
    defineMultiRules(multi);

    multi.use("AFCS");
  },
  manageSetup(
    settingsEl: JQuery,
    {
      conference,
      saveConference,
      hierarchyNames,
      updateConfDate
    }: EasyCVCareerConferenceManageArgs,
    user: User
  ) {
    const ccManage = new CCManage(conference, saveConference, updateConfDate, {
      includeSections: {
        cutoff: true,
        mentees: true,
        goals: false,
        questions: false,
        mentors: true,
        reminders: false,
        years: true
      },
      sortBy: "mentor",
      verbage: {
        mentor: "Mentor",
        mentee: "Mentee"
      }
    });
    ccManage.createSettingsElements(settingsEl);
    updateConfDate(conference);
    ccManage.createMentorsMenteesSection();
    ccManage.createAwesomplete();
    ccManage.hookMessageButton(user);
    ccManage.populateMenteesSection(hierarchyNames);

    // Add custom toggle for afcs

    const [open, _] = getAnnualCounselingTabOpen(conference);
    // Not testing if `open` is true or false, testing if `null` was returned, indicating something went wrong
    if (open !== null) {
      $("#careerYearToggle").after(customToggleHTML(open));
      bindCustomToggle(conference, saveConference);
    }
  }
} satisfies EasyCVCareerConference;

const AFCS_TAB_OPEN_KEY = "annualCounselingTabOpen";
/*
 * No data is treated as true. A return of `null` should be treated as some error
 */
const getAnnualCounselingTabOpen = (
  conference: Conference
): [open: boolean | null, viewYear: number] => {
  const viewYear = dayjs($("#careerYearTitle").text().split("-")[0]).year();
  // Probably excessive
  if (
    !Number.isNaN(viewYear) &&
    Number.isFinite(viewYear) &&
    viewYear in conference.openYears
  ) {
    const val = conference.openYears[viewYear]?.[AFCS_TAB_OPEN_KEY];
    // default to false
    return [val === true, viewYear];
  }
  // something undefined happened
  return [null, viewYear];
};

/*
 * Returns the html for the custom toggle block. Sets the two buttons' classes depending on `open`
 */
const customToggleHTML = (open: boolean) => {
  let html =
    '<h4 style="display: inline-block; margin: 8px 10px 15px 25px; vertical-align: top;">Annual Counseling Tab</h4>';
  html +=
    '<div id="annualCounselingTabToggle" class="btn-group" role="group" style="display: inline-block; vertical-align: top;">';
  html += `<button class="annualCounselingOn btn btn-${open ? "success" : "default"}" type="button">On</button>`;
  html += `<button class="annualCounselingOff btn btn-${!open ? "danger" : "default"}" type="button">Off</button>`;
  return html + "</div>";
};

/* Binds input events on the custom toggle buttons. */
const bindCustomToggle = (
  conference: Conference,
  saveConference: EasyCVCareerConferenceManageArgs["saveConference"]
) => {
  $(".annualCounselingOff")
    .off("click")
    .on("click", () => {
      const [open, viewYear] = getAnnualCounselingTabOpen(conference);
      if (open !== null && open) {
        conference.openYears[viewYear][AFCS_TAB_OPEN_KEY] = false;
        $(".annualCounselingOff")
          .removeClass("btn-default")
          .addClass("btn-danger");
        $(".annualCounselingOn")
          .removeClass("btn-success")
          .addClass("btn-default");
        saveConference(conference);
      }
    });
  $(".annualCounselingOn")
    .off("click")
    .on("click", () => {
      const [open, viewYear] = getAnnualCounselingTabOpen(conference);
      if (open !== null && !open) {
        conference.openYears[viewYear][AFCS_TAB_OPEN_KEY] = true;
        $(".annualCounselingOn")
          .removeClass("btn-default")
          .addClass("btn-success");
        $(".annualCounselingOff")
          .removeClass("btn-danger")
          .addClass("btn-default");
        saveConference(conference);
      }
    });
};
